import { formatBytes } from "@wirevod/app-core";
import { IconButton, ProgressBar } from "@wirevod/ui";
import { useRef, useState } from "react";
import { Link } from "react-router";
import type { UploadItem } from "./manager";
import { formatEta, formatRate } from "./speed";
import styles from "./UploadPanel.module.css";
import { useUploadManager, useUploads } from "./UploadProvider";

const ACTIVE = new Set(["queued", "uploading"]);

function percent(item: UploadItem): number {
  return item.size ? Math.min(100, Math.round((item.uploadedBytes / item.size) * 100)) : 0;
}

function detail(item: UploadItem): string {
  switch (item.status) {
    case "queued":
      return "Waiting to start…";
    case "uploading": {
      const parts = [
        `${percent(item)}%`,
        `${formatBytes(item.uploadedBytes)} of ${formatBytes(item.size)}`,
      ];
      if (item.rate) {
        parts.push(formatRate(item.rate));
        const eta = formatEta((item.size - item.uploadedBytes) / item.rate);
        if (eta) parts.push(eta);
      }
      return parts.join(" · ");
    }
    case "paused":
      return `Paused at ${percent(item)}%`;
    case "interrupted":
      return "Interrupted. Choose the same file to continue.";
    case "done":
      return "Uploaded. Preparing for playback.";
    case "failed":
      return item.error ?? "Upload failed.";
    case "canceled":
      return "Canceled";
  }
}

const icon = {
  pause: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M4 3h3v10H4zM9 3h3v10H9z" />
    </svg>
  ),
  play: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M4.5 2.75v10.5L13 8z" />
    </svg>
  ),
  close: (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  ),
  chevron: (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M4 10l4-4 4 4" />
    </svg>
  ),
};

function Row({ item }: { item: UploadItem }) {
  const manager = useUploadManager();
  const picker = useRef<HTMLInputElement>(null);
  const [mismatch, setMismatch] = useState(false);
  const tone = item.status === "failed" ? "danger" : item.status === "done" ? "success" : "accent";

  return (
    <li className={styles.row} data-status={item.status}>
      <div className={styles.rowHead}>
        <span className={styles.name} title={item.fileName}>
          {item.title}
        </span>
        <span className={styles.actions}>
          {item.status === "uploading" ? (
            <IconButton
              aria-label={`Pause ${item.title}`}
              size="sm"
              variant="ghost"
              onClick={() => manager.pause(item.key)}
            >
              {icon.pause}
            </IconButton>
          ) : null}
          {item.status === "paused" || item.status === "failed" ? (
            <IconButton
              aria-label={`Resume ${item.title}`}
              size="sm"
              variant="ghost"
              onClick={() => manager.resume(item.key)}
            >
              {icon.play}
            </IconButton>
          ) : null}
          {ACTIVE.has(item.status) || item.status === "paused" || item.status === "failed" ? (
            <IconButton
              aria-label={`Cancel ${item.title}`}
              size="sm"
              variant="ghost"
              onClick={() => void manager.cancel(item.key)}
            >
              {icon.close}
            </IconButton>
          ) : (
            <IconButton
              aria-label={`Dismiss ${item.title}`}
              size="sm"
              variant="ghost"
              onClick={() => manager.dismiss(item.key)}
            >
              {icon.close}
            </IconButton>
          )}
        </span>
      </div>
      {item.status !== "canceled" && item.status !== "interrupted" ? (
        <ProgressBar
          label={`${item.title} upload progress`}
          hideLabel
          size="sm"
          value={item.status === "queued" ? null : percent(item)}
          tone={tone}
        />
      ) : null}
      <div className={styles.detail}>
        <span aria-live={item.status === "failed" ? "assertive" : undefined}>
          {mismatch ? "That's a different file. Choose the original to continue." : detail(item)}
        </span>
        {item.status === "done" && item.assetId ? (
          <Link to={`/assets/${item.assetId}`}>View</Link>
        ) : null}
        {item.status === "interrupted" ? (
          <>
            <button type="button" className={styles.choose} onClick={() => picker.current?.click()}>
              Choose file
            </button>
            <input
              ref={picker}
              type="file"
              accept="video/*"
              hidden
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                if (file) setMismatch(!manager.reattach(item.key, file));
              }}
            />
          </>
        ) : null}
      </div>
    </li>
  );
}

/** Floating panel listing background uploads (bottom-right, collapsible). */
export function UploadPanel() {
  const items = useUploads();
  const manager = useUploadManager();
  const [collapsed, setCollapsed] = useState(false);
  if (items.length === 0) return null;

  const active = items.filter((i) => ACTIVE.has(i.status)).length;
  const done = items.filter((i) => i.status === "done").length;
  const title = active
    ? `Uploading ${active} ${active === 1 ? "video" : "videos"}`
    : done
      ? `${done} ${done === 1 ? "upload" : "uploads"} complete`
      : "Uploads";

  return (
    <section className={styles.panel} aria-label="Uploads" data-collapsed={collapsed}>
      <header className={styles.header}>
        <h2 className={styles.title} aria-live="polite">
          {title}
        </h2>
        <IconButton
          aria-label={collapsed ? "Expand uploads" : "Collapse uploads"}
          size="sm"
          variant="ghost"
          onClick={() => setCollapsed((c) => !c)}
        >
          <span className={styles.chevron}>{icon.chevron}</span>
        </IconButton>
        {active === 0 ? (
          <IconButton
            aria-label="Close uploads"
            size="sm"
            variant="ghost"
            onClick={() => manager.clearFinished()}
          >
            {icon.close}
          </IconButton>
        ) : null}
      </header>
      {collapsed ? null : (
        <ul className={styles.list}>
          {items.map((item) => (
            <Row key={item.key} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}
